import React, { useEffect, useMemo, useRef, useState } from 'react';
import { storage } from '@/utils/utils';
import { OPINION_GUIDE_BOX_LIST4 } from '@home/components/GuideModule/GuideOpinionBox';
import SvgIcon from '@home/components/SvgIcon';
import { FilterFixed, useFilterDataHook } from '@op/components/FilterCommon';
import { useRequestFontWebConfig } from '@op/hooks/useFrontWebConfig';
import useLanguage from '@op/hooks/useLanguage';
import { useInitBuriedPointInfo } from '@op/operationLog/buriedPoint';
import { VideoPoint } from '@op/operationLog/pages';
import { Bubble, Col, Row, Segment } from 'tea-component';
import { GUIDE_TYPE_OPINION_COMMUNITY, useDispatch } from 'umi';
import AiImg from '@op/assets/svg/icon-ai.svg';
import AiImgWhite from '@op/assets/svg/icon-ai-white.svg';
import { DBO_TM } from '@op/utils/format';
import CardLoading from '@op/components/CardLoading';
import VideoCard from '@/pages/opinion/pages/community/video/components/VideoCard/card';
import { useDownloadParams } from '@op/hooks/useDownloadParams';
import {
  dealwith8BallParams,
  formatAITagsFilterValue,
  isSpecialEdition,
} from '../components/utils';
import CardCommon from '@op/components/CardCommon';
import VideoTable from '../components/VideoTable';
import FilterBoxCommon from '@op/components/FilterBoxCommon';
import { communityVideoChannelText } from '@op/components/BubbleCommon';

type TabType = 'video' | 'ai' | 'ai_danmaku' | 'ai_tag';
const localStorageKey = 'aiVideoTipsVisible';

const VideoStatistics = (props: {
  gameInfo: any;
  wholeGameInfo: any;
  userInfo: any;
  isSupportVideoTags: boolean;
}) => {
  const { gameInfo, wholeGameInfo, userInfo, isSupportVideoTags } = props;
  const [tab, setTab] = useState<TabType>('video');

  const [visible, setVisible] = useState(
    localStorage.getItem(localStorageKey) !== 'true',
  );

  function handleVisibleChange(e: any) {
    localStorage.setItem(localStorageKey, 'true');
    setVisible(false);
    e.stopPropagation();
  }

  const tableRef = useRef<HTMLDivElement>(null);

  const dispatch = useDispatch();

  useRequestFontWebConfig({
    keys: [{ key: 'ai_prompt_list' }],
    gameInfo,
  });

  useEffect(() => {
    const list = storage.getLocalStorageItem(OPINION_GUIDE_BOX_LIST4) || [];
    if (userInfo?.openid && !list.includes(userInfo.openid)) {
      dispatch({
        type: 'guide/save',
        payload: {
          guideSys: '',
          guideType: GUIDE_TYPE_OPINION_COMMUNITY,
        },
      });
    }
  }, [userInfo]);

  const { formatMessage } = useLanguage();
  const { download, downloading } = useDownloadParams();
  useInitBuriedPointInfo({ buriedPoint: VideoPoint });
  const [tableParams, setTableParams] = useState({
    sort_item: 'comment_time',
    sort_model: 'desc',
    total: 0,
    search_content: '',
  });

  function scrollTableInfo(value: TabType, hasVideoAITag: boolean) {
    if (tableRef.current) {
      const tableCurrentBox =
        tableRef.current?.querySelector('.tea-table__body');

      if (tableCurrentBox) {
        const valueMap = {
          video: 0,
          ai: hasVideoAITag ? 1870 : 1370,
          ai_danmaku: 1870,
          ai_tag: 1370,
        };
        tableCurrentBox.scrollLeft = valueMap[value] || 0;
      }
    }
  }

  const {
    loading,
    filter,
    changeFilter,
    filterOptions: filterOptionsHook,
    scrollBody,
    changeOtherPageParams,
  } = useFilterDataHook({
    gameInfo,
    hasHero: true,
    hasVideoTag: true,
    pageName: 'Community_Video',
  });

  const keepParams = useMemo(() => {
    const keepParams = {
      ...filter,
      ...gameInfo,
    };

    if (isSupportVideoTags) {
      return {
        ...keepParams,
        video_tags: formatAITagsFilterValue(filter?.video_tags),
        need_video_tags: true,
      };
    }
    return keepParams;
  }, [gameInfo, filter, isSupportVideoTags]);

  const { hasVideoAITag, segmentOptions } = useMemo(() => {
    const hasVideoAITag = isSupportVideoTags;
    const segment = [
      {
        text: formatMessage({ id: 'Video Metrics' }),
        value: 'video',
      },
      {
        text: (
          <Bubble
            overlayClassName="dbo-bubble-unlimited-width"
            dark
            visible={visible}
            content={
              <div className="db-bubble-close-wrap">
                {formatMessage({
                  id: 'Added AI Tag, Click to View',
                })}
                <span
                  className="db-bubble-close-icon"
                  onClick={(e) => handleVisibleChange(e)}
                >
                  <SvgIcon type="clonse"></SvgIcon>
                </span>
              </div>
            }
          >
            <div className="dbo-inlineflex-w16">
              <img
                src={tab === 'ai_tag' ? AiImgWhite : AiImg}
                className="dbo-inlineflex-w16-img"
              />
              {formatMessage({
                id: 'AI Tags',
              })}
            </div>
          </Bubble>
        ),
        value: 'ai_tag',
      },
      {
        text: (
          <Bubble
            overlayClassName="dbo-bubble-unlimited-width"
            dark
            visible={hasVideoAITag ? false : visible}
            content={
              <div className="db-bubble-close-wrap">
                {formatMessage({
                  id: 'Added AI Video Content Summary, Click to View',
                })}
                <span
                  className="db-bubble-close-icon"
                  onClick={(e) => handleVisibleChange(e)}
                >
                  <SvgIcon type="clonse"></SvgIcon>
                </span>
              </div>
            }
          >
            <div className="dbo-inlineflex-w16">
              <img
                src={tab === 'ai' ? AiImgWhite : AiImg}
                className="dbo-inlineflex-w16-img"
              />
              {formatMessage({ id: 'AI Content Summary' })}
            </div>
          </Bubble>
        ),
        value: 'ai',
      },
      {
        text: (
          <div className="dbo-inlineflex-w16">
            <img
              src={tab === 'ai_danmaku' ? AiImgWhite : AiImg}
              className="dbo-inlineflex-w16-img"
            />
            {formatMessage({
              id: 'AI Live Stream Comments Summary',
            })}
          </div>
        ),
        value: 'ai_danmaku',
      },
    ];

    return {
      hasVideoAITag,
      segmentOptions: segment.filter(
        (val) => val.value !== (hasVideoAITag ? 'ai_danmaku' : 'ai_tag'),
      ),
    };
  }, [gameInfo, visible, isSupportVideoTags, tab, formatMessage]);

  const filterOptions = useMemo(() => {
    let filterOptions = filterOptionsHook;
    if (!isSupportVideoTags) {
      filterOptions = filterOptionsHook?.filter((val) => val !== 'videoAITag');
    }
    return filterOptions;
  }, [filterOptionsHook, isSupportVideoTags]);

  const [search, setSearch] = useState('');
  const [language, setLanguage] = useState('origin');
  const [showTagsOnly, setShowTagsOnly] = useState(false);

  return (
    <div ref={scrollBody}>
      <FilterFixed
        filter={filterOptions}
        confirm={(value) => {
          changeFilter({
            ...value,
            media_types: ['video', 'live'],
            review_types: ['posts_video', 'stream_titles'],
          });
        }}
        finallyDealwithDefaultValue={(
          value,
          otherPageParams,
          isDefaultValue = false,
        ) => {
          !isDefaultValue && changeOtherPageParams(otherPageParams || {});
          return value;
        }}

        defaultConfig={{
          channel: {
            channelKey: 'social',
            tips: isSpecialEdition(gameInfo)
              ? communityVideoChannelText
              : undefined,
          },
          date: {
            permanentTipsKey: 'video',
          },
        }}
        defaultValue={DBO_TM.utcFilterRangeDay(6, 0)}
      ></FilterFixed>
      <CardLoading loading={loading}>
        <Row className="dbo-video-card">
          <Col span={8}>
            <VideoCard
              gameInfo={gameInfo}
              type="counts"
              keepParams={keepParams}
            />
          </Col>
          <Col span={8}>
            <VideoCard
              gameInfo={gameInfo}
              type="engagements"
              keepParams={keepParams}
            />
          </Col>
          <Col span={8}>
            <VideoCard
              gameInfo={gameInfo}
              type="views"
              keepParams={keepParams}
            />
          </Col>
        </Row>
        <FilterBoxCommon
          downloadLoading={downloading}
          left={[
            {
              key: 'search',
              element: (
                <Segment
                  className="segment-light"
                  value={tab}
                  onChange={(value: any) => {
                    setTab(value);
                    scrollTableInfo(value, hasVideoAITag);
                  }}
                  options={segmentOptions}
                />
              ),
            },
          ]}
          right={[
            ...(hasVideoAITag
              ? [
                {
                  key: 'singleCheckbox',
                  valueKey: 'showTagsOnly',
                  defaultValue: showTagsOnly,
                  defaultParams: { text: 'Show Tagged Videos Only' },
                },
              ]
              : []),
            {
              key: 'search',
              defaultParams: {
                placeholder: formatMessage({
                  id: 'Search video name',
                }),
                onSearch: (value: any) => {
                  setSearch(value.trim());
                },
                className: 'dbo-video-search',
                onClear: () => {
                  setSearch('');
                },
              },
            },
            {
              key: 'download',
              event: {
                onClick: () => {
                  if (!tableParams?.total) return;

                  download({
                    key: 'VideoTableDownload',
                    fileName: 'Video',
                    filter: dealwith8BallParams({
                      ...keepParams,
                      ...gameInfo,
                      ...tableParams,
                    }),
                    auxiliaryParams: {
                      hasExtInfo: wholeGameInfo?.has_video_ext_info,
                      hasVideoAITag,
                      hasEngagementRate: isSpecialEdition(gameInfo),
                    },
                  });
                },
              },
            },
          ]}
          onChange={(value, key) => {
            const { showTagsOnly } = value;
            if (key === 'showTagsOnly') {
              setShowTagsOnly(showTagsOnly);
            }
          }}
          className={'dbo-video-filter-box-common'}
        ></FilterBoxCommon>

        <CardCommon>
          <VideoTable
            tableRef={tableRef}
            keepParams={keepParams}
            gameInfo={gameInfo}
            wholeGameInfo={wholeGameInfo}
            language={language}
            search={search}
            setLanguage={setLanguage}
            setTableParams={setTableParams}
            showTagsOnly={showTagsOnly}
          />
        </CardCommon>
      </CardLoading>
    </div>
  );
};

export default VideoStatistics;
